import { SimpleGrid } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FacultyFilter } from '@/module/Nomen/Faculty/FacultyFilter';
import {
  TravelActivityType,
  TravelRegistryType,
  TravelRequestStatus,
  travelActivityTypeList,
  travelRegistryTypeList,
  travelRequestStatusList,
} from '@/module/Travel/Request/model/TravelRequestTypes';

import { NumberFilter, useNumberFilter } from '@/core/components';
import { CustomMultiSelect } from '@/core/components/MultiSelect';
import { CustomSelect } from '@/core/components/Select';

import { RoleType, useCurrentUser } from '@feature/security';
import { RO_DATE_FORMAT } from '@feature/utils';

import { getTravelOfficeRegistryType } from '../travelRoles';
import { TravelRegistryFilter } from './model/TravelRegistryTypes';
import { useTravelFacultyFilter } from './useTravelFacultyFilter';

interface UseTravelRequestFiltersOptions {
  initialYear: number | null;
  // A hidden status filter also drops its selection from the request.
  showStatuses: boolean;
  restrictDeanFaculty: boolean;
  yearDescription?: string;
  // Called on every filter change, so a narrower filter never lands on a page past its last one.
  onChange: () => void;
}

interface UseTravelRequestFiltersReturn {
  filter: TravelRegistryFilter;
  fields: ReactNode;
}

// Every travel list shares these filters so they keep the same set for every role.
export function useTravelRequestFilters({
  initialYear,
  showStatuses,
  restrictDeanFaculty,
  yearDescription,
  onChange,
}: UseTravelRequestFiltersOptions): UseTravelRequestFiltersReturn {
  const { t } = useTranslation();
  const { item: registrationYear, value: yearValue, onChange: setYear } = useNumberFilter(initialYear ?? '');
  const { currentRole } = useCurrentUser();
  // The pick is kept with the role it was made under, so a role switch falls back to the new role's own registry.
  const [registryPick, setRegistryPick] = useState<{ role: RoleType; value: TravelRegistryType | null } | null>(null);
  const registryType =
    registryPick?.role === currentRole ? registryPick.value : getTravelOfficeRegistryType(currentRole);
  const [statuses, setStatuses] = useState<TravelRequestStatus[]>([]);
  const [activityTypes, setActivityTypes] = useState<TravelActivityType[]>([]);
  const { facultyId, facultyFilterProps } = useTravelFacultyFilter(restrictDeanFaculty);
  const [departureFrom, setDepartureFrom] = useState<string | null>(null);
  const [departureTo, setDepartureTo] = useState<string | null>(null);

  function changing<T>(set: (value: T) => void) {
    return (value: T) => {
      set(value);
      onChange();
    };
  }

  const filter: TravelRegistryFilter = {
    // A half-typed year debounces to NaN, which qs would still send.
    registrationYear: registrationYear != null && Number.isFinite(registrationYear) ? registrationYear : null,
    registryType,
    statuses: showStatuses ? statuses : [],
    activityTypes,
    facultyId,
    departureFrom,
    departureTo,
  };

  const registryTypeOptions = travelRegistryTypeList.map(type => ({
    value: type,
    label: t(`travel.registryTypeEnum.${type}`),
  }));
  const statusOptions = travelRequestStatusList.map(status => ({
    value: status,
    label: t(`travel.statusEnum.${status}`),
  }));
  const activityTypeOptions = travelActivityTypeList.map(type => ({
    value: type,
    label: t(`travel.activityTypeEnum.${type}`),
  }));

  const fields = (
    <>
      {/* Alone on its row because the registry names are too long for a grid column. */}
      <CustomSelect
        label={t('travel.registry.filters.registryType')}
        placeholder={t('travel.registry.filters.registryTypeAll')}
        data={registryTypeOptions}
        value={registryType}
        onChange={changing((value: string | null) =>
          setRegistryPick({ role: currentRole, value: value as TravelRegistryType | null }),
        )}
        clearable
      />
      {/* Bottom alignment keeps the inputs level when one field carries a description. */}
      <SimpleGrid
        cols={{ base: 1, sm: showStatuses ? 3 : 2 }}
        style={{ alignItems: 'end' }}
      >
        <NumberFilter
          label={t('travel.registry.filters.registrationYear')}
          description={yearDescription}
          allowDecimal={false}
          min={2000}
          value={yearValue}
          onChange={changing(setYear)}
        />
        {showStatuses && (
          <CustomMultiSelect
            label={t('travel.registry.filters.statuses')}
            placeholder={t('travel.registry.filters.statuses')}
            data={statusOptions}
            value={statuses}
            onChange={changing((value: string[]) => setStatuses(value as TravelRequestStatus[]))}
            clearable
          />
        )}
        <CustomMultiSelect
          label={t('travel.registry.filters.activityTypes')}
          placeholder={t('travel.registry.filters.activityTypes')}
          data={activityTypeOptions}
          value={activityTypes}
          onChange={changing((value: string[]) => setActivityTypes(value as TravelActivityType[]))}
          clearable
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: facultyFilterProps ? 3 : 2 }}>
        {facultyFilterProps && (
          <FacultyFilter
            label={t('travel.registry.filters.faculty')}
            {...facultyFilterProps}
            onChange={changing(facultyFilterProps.onChange)}
          />
        )}
        <DateInput
          label={t('travel.registry.filters.departureFrom')}
          placeholder={t('travel.registry.filters.departureFrom')}
          valueFormat={RO_DATE_FORMAT}
          value={departureFrom}
          onChange={changing(setDepartureFrom)}
          clearable
        />
        <DateInput
          label={t('travel.registry.filters.departureTo')}
          placeholder={t('travel.registry.filters.departureTo')}
          valueFormat={RO_DATE_FORMAT}
          value={departureTo}
          onChange={changing(setDepartureTo)}
          clearable
        />
      </SimpleGrid>
    </>
  );

  return { filter, fields };
}
